import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
}

type View = 'login' | 'forgot-password' | 'forgot-success';

export default function LoginModal({ isOpen, onClose, onSwitchToRegister }: LoginModalProps) {
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('login');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    // Resetear completamente el modal al cerrarse
    setView('login');
    setFormData({ email: '', password: '' });
    setRecoveryEmail('');
    setError('');
    setLoading(false);
    onClose();
  };

  const handleSubmitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validación de campos
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('El formato del email no es válido.');
      return;
    }

    if (!formData.password.trim()) {
      setError('La contraseña no puede estar vacía.');
      return;
    }

    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      
      // Redirigir según tipo de usuario
      if (formData.email === 'beaaraquee@gmail.com') {
        // Admin: redirigir a /admin
        navigate('/admin');
      } else if (result?.user) {
        // Usuario normal: redirigir según userType en metadata
        const userType = result.user.user_metadata?.userType;
        if (userType === 'empresa') {
          navigate('/empresa');
        } else {
          navigate('/perfil');
        }
      }
      
      handleClose();
    } catch (err: any) {
      // Mensajes de error específicos
      const errorMessage = err.message || 'Error desconocido';
      
      if (errorMessage.includes('Email o contraseña incorrectos') || 
          errorMessage.includes('Invalid login credentials')) {
        setError('Email o contraseña incorrectos. Por favor, verifica tus credenciales.');
      } else if (errorMessage.includes('Email not confirmed') || 
                 errorMessage.includes('verifica tu email')) {
        setError('Tu email aún no ha sido verificado. Revisa tu bandeja de entrada.');
      } else if (errorMessage.includes('Too many requests') || 
                 errorMessage.includes('rate limit')) {
        setError('Demasiados intentos. Por favor, espera unos minutos e inténtalo de nuevo.');
      } else if (errorMessage.includes('Network') || 
                 errorMessage.includes('fetch')) {
        setError('Error de conexión. Verifica tu internet e inténtalo de nuevo.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!recoveryEmail.trim()) {
      setError('Por favor, introduce tu email.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recoveryEmail)) {
      setError('El formato del email no es válido.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(recoveryEmail);
      setView('forgot-success');
    } catch (err: any) {
      setError('Error al enviar el email de recuperación. Inténtalo de nuevo.');
      console.error('Error en recuperación de contraseña:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Vista: Login ──────────────────────────────────────────────
  if (view === 'login') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Iniciar sesión</h2>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmitLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Contraseña</label>
                  <button
                    type="button"
                    onClick={() => { setError(''); setView('forgot-password'); }}
                    className="text-xs text-[#1A2B4A] hover:underline cursor-pointer"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  placeholder="Tu contraseña"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#1A2B4A] text-white font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Iniciando sesión...
                  </span>
                ) : 'Iniciar sesión'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                ¿No tienes cuenta?{' '}
                <button onClick={onSwitchToRegister} className="text-[#1A2B4A] font-medium hover:underline cursor-pointer">
                  Regístrate
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Vista: Recuperar contraseña ───────────────────────────────
  if (view === 'forgot-password') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="p-8">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => { setError(''); setView('login'); }}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-2xl"></i>
              </button>
              <h2 className="text-2xl font-bold text-gray-900">Recuperar contraseña</h2>
              <button onClick={handleClose} className="ml-auto text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Introduce tu email y te enviaremos las instrucciones para restablecer tu contraseña.
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmitRecovery} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#1A2B4A] text-white font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </span>
                ) : 'Enviar instrucciones'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                ¿Recuerdas tu contraseña?{' '}
                <button
                  onClick={() => { setError(''); setView('login'); }}
                  className="text-[#1A2B4A] font-medium hover:underline cursor-pointer"
                >
                  Volver al inicio de sesión
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Vista: Confirmación enviada ───────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-8 text-center">
          <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
            <i className="ri-close-line text-2xl"></i>
          </button>

          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <i className="ri-mail-check-line text-3xl text-green-600"></i>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-3">Revisa tu email</h2>
          <p className="text-sm text-gray-600 leading-relaxed mb-2">
            Si existe una cuenta asociada a{' '}
            <span className="font-medium text-gray-800">{recoveryEmail}</span>,
            recibirás un email con las instrucciones para restablecer tu contraseña.
          </p>
          <p className="text-xs text-gray-400 mb-8">Revisa también tu carpeta de spam.</p>

          <button
            onClick={() => { setView('login'); setRecoveryEmail(''); }}
            className="w-full py-3 bg-[#1A2B4A] text-white font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors whitespace-nowrap cursor-pointer"
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
}